import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Slice {
  id: string;
  item: string;
  sliceGoal: string;
  definitionOfDone: string;
  risksBurningDown: string;
  dependenciesResolved: string;
  owner: string;
  timeStart: string;
  timeEnd: string;
}

export default function SequenceSlicePage() {
  const [slices, setSlices] = useState<Slice[]>([
    { id: '1', item: '', sliceGoal: '', definitionOfDone: '', risksBurningDown: '', dependenciesResolved: '', owner: '', timeStart: '', timeEnd: '' },
    { id: '2', item: '', sliceGoal: '', definitionOfDone: '', risksBurningDown: '', dependenciesResolved: '', owner: '', timeStart: '', timeEnd: '' },
    { id: '3', item: '', sliceGoal: '', definitionOfDone: '', risksBurningDown: '', dependenciesResolved: '', owner: '', timeStart: '', timeEnd: '' },
  ]);

  const updateSlice = (id: string, field: keyof Slice, value: string) => {
    setSlices(slices.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  return (
    <TaskPage
      phase="prioritize"
      title="Sequence & Slice"
      pageNumber={3}
      instruction="Decide the first meaningful slice for each Now item—something a user/stakeholder can experience in ≤ 2 weeks."
      whyItMatters="Front-load risk-reduction and clear 'done' definitions; worked examples are classic scaffolds for novices."
      coachRail={{
        timing: "15 minutes",
        tips: "Each slice should be something a user/stakeholder can experience in ≤ 2 weeks.",
        debrief: "Are the slices meaningful? Can stakeholders experience them soon?"
      }}
    >
      <div className="space-y-6">
        {slices.map((slice, index) => (
          <div key={slice.id} className="bg-[var(--surface)] p-6 border border-[var(--rule)]">
            <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Slice {index + 1}</h3>
            <div className="space-y-4">
              <TextInput
                label="Item"
                value={slice.item}
                onChange={(e) => updateSlice(slice.id, 'item', e.target.value)}
                placeholder="Which Now item?"
              />
              <TextArea
                label="Slice goal (outcome)"
                value={slice.sliceGoal}
                onChange={(e) => updateSlice(slice.id, 'sliceGoal', e.target.value)}
                placeholder="What outcome will this slice achieve?"
                rows={2}
              />
              <TextArea
                label="Definition of 'done'"
                value={slice.definitionOfDone}
                onChange={(e) => updateSlice(slice.id, 'definitionOfDone', e.target.value)}
                placeholder="What does 'done' mean for this slice?"
                rows={2}
              />
              <TextArea
                label="Risks we're burning down first"
                value={slice.risksBurningDown}
                onChange={(e) => updateSlice(slice.id, 'risksBurningDown', e.target.value)}
                placeholder="What risks are we addressing first?"
                rows={2}
              />
              <TextInput
                label="Dependencies resolved by"
                value={slice.dependenciesResolved}
                onChange={(e) => updateSlice(slice.id, 'dependenciesResolved', e.target.value)}
                placeholder="When/by whom?"
              />
              <div className="grid grid-cols-3 gap-4">
                <TextInput
                  label="Owner"
                  value={slice.owner}
                  onChange={(e) => updateSlice(slice.id, 'owner', e.target.value)}
                  placeholder="Who owns this?"
                />
                <TextInput
                  label="Time fence start"
                  type="date"
                  value={slice.timeStart}
                  onChange={(e) => updateSlice(slice.id, 'timeStart', e.target.value)}
                />
                <TextInput
                  label="Time fence end"
                  type="date"
                  value={slice.timeEnd}
                  onChange={(e) => updateSlice(slice.id, 'timeEnd', e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Callout type="tip" phase="prioritize">
        <p className="text-sm">Each slice should be something a user/stakeholder can experience in ≤ 2 weeks. Focus on meaningful outcomes, not just features.</p>
      </Callout>
    </TaskPage>
  );
}






